import { Email, LocalShipping } from '@mui/icons-material';
import { Button, Grid, Typography } from '@mui/material';
import { alpha, Box } from '@mui/system';
import KivraIcon from 'src/assets/images/Kivra-icon.png';
import { useTranslation } from 'react-i18next';
import { useInvoiceContext } from 'src/shared/contexts/invoiceContext';
import { isFeatureEnabled } from 'src/shared/functions/isFeatureEnabled';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { InvoiceDeliveryMethodIntegerEnum } from 'src/shared/models/enums/invoiceDeliveryMethodInteger.enum';
import { useCallback, useEffect, useState } from 'react';
import { InvoiceGroup } from 'src/shared/models/invoiceGroup/invoiceGroup';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getInvoiceGroupById } from 'src/shared/services/invoiceGroupService';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';

export interface InvoiceDeliveryMethodItem {
  label: string;
  count: number;
  key: InvoiceDeliveryMethodIntegerEnum;
  icon: JSX.Element;
  disabled: boolean;
}

type CreateInvoiceSelectInvoiceDeliveryMethodProps = {
  deliveryMethodCallback: (val: InvoiceDeliveryMethodIntegerEnum) => void;
  openDeliveryMethodModal: boolean;
  onSave: () => void;
  onClose: () => void;
  invoiceGroupId: number;
};

export const CreateInvoiceSelectInvoiceDeliveryMethod = (props: CreateInvoiceSelectInvoiceDeliveryMethodProps) => {
  const { deliveryMethodCallback, openDeliveryMethodModal, onClose, onSave, invoiceGroupId } = props;
  const isMounted = useIsMounted();
  const { invoiceCountStats } = useInvoiceContext();
  const { t } = useTranslation(['invoice']);

  const [loading, setLoading] = useState(false);
  const [invoiceGroup, setInvoiceGroup] = useState<InvoiceGroup>();

  const fetchInvoiceGroup = useCallback(async () => {
    setLoading(true);

    try {
      const res = await getInvoiceGroupById(invoiceGroupId);
      if (isMounted()) {
        setInvoiceGroup(res.data);
      }
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      setLoading(false);
    }
  }, [invoiceGroupId, isMounted]);

  useEffect(() => {
    if (isMounted()) {
      fetchInvoiceGroup();
    }
  }, [fetchInvoiceGroup, isMounted]);

  const createInvoiceMissionTypes: InvoiceDeliveryMethodItem[] = [
    {
      label: t('invoice:invoiceDeliveryMethod.mail'),
      key: InvoiceDeliveryMethodIntegerEnum.mail,
      icon: <Email />,
      count: invoiceCountStats?.emailCount || 0,
      disabled: false,
    },
    // {
    //   label: t('invoice:invoiceDeliveryMethod.letter'),
    //   key: InvoiceDeliveryMethodIntegerEnum.letter,
    //   icon: <LocalShipping />,
    //   count: invoiceCountStats?.totalCount || 0,
    // },
    ...(isFeatureEnabled()
      ? [
          {
            label: t('invoice:invoiceDeliveryMethod.letter'),
            key: InvoiceDeliveryMethodIntegerEnum.letter,
            icon: <LocalShipping />,
            count: invoiceCountStats?.totalCount || 0,
            disabled: false,
          },
        ]
      : []),
    // {
    //   label: t('invoice:invoiceDeliveryMethod.sms'),
    //   key: InvoiceDeliveryMethodIntegerEnum.sms,
    //   icon: <Smartphone />,
    //   count: invoiceCountStats?.mobilePhoneCount || 0,
    // },
    {
      label: t('invoice:invoiceDeliveryMethod.kivra'),
      key: InvoiceDeliveryMethodIntegerEnum.kivra,
      icon: <Box component='img' src={KivraIcon} sx={{ maxWidth: '2.3rem', mt: 0.5 }} alt='Kivra logo' />,
      count: 0,
      disabled: !(invoiceGroup?.kivraTenantKey && invoiceGroup.kivraTenantKey.length > 0),
    },
  ];

  return (
    <GenericDialog
      open={openDeliveryMethodModal}
      padding={0}
      disableBackdropClosing
      dialogTitle={t('invoice:createInvoice:selectInvoiceDeliveryMethod')}
      maxWidth='sm'
      onClose={onClose}
      onSave={onSave}
      showDialogActions={false}
      dialogContent={
        <Grid container mt={3}>
          <LoadingOverlay loading={loading} />
          {createInvoiceMissionTypes.map((mission) => (
            <Grid item xs={12} key={mission.key} px={5}>
              <Button
                color='success'
                variant='outlined'
                fullWidth
                // disabled={mission.count === 0}
                sx={{
                  py: 2,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                  height: '80%',
                  backgroundColor: '#fff',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    backgroundColor: (theme) => alpha(theme.palette.success.light, 0.2),
                  },
                  '& .MuiSvgIcon-root': {
                    width: '2.5rem',
                    height: '2.5rem',
                    opacity: 0.4,
                  },
                  '&:disabled': {
                    backgroundColor: '#f6f6f6',
                  },
                  '&.selected': {
                    borderWidth: 3,
                    backgroundColor: (theme) => alpha(theme.palette.success.light, 0.2),
                  },
                  '&:disabled::after': {
                    position: 'absolute',
                    content: '" "',
                    display: 'block',
                    width: '100%',
                    left: 0,
                    right: 0,
                    top: 0,
                    bottom: 0,
                    margin: 'auto',
                    opacity: 0.5,
                    background: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' version='1.1' preserveAspectRatio='none' viewBox='0 0 100 100'><path d='M100 0 L0 100 ' stroke='lightgrey' stroke-width='1'/><path d='M0 0 L100 100 ' stroke='lightgrey' stroke-width='1'/></svg>")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center center',
                    backgroundSize: '100% 100%, auto',
                  },
                }}
                onClick={() => deliveryMethodCallback(mission.key as InvoiceDeliveryMethodIntegerEnum)}
                disabled={mission.disabled}
              >
                {mission.icon}
                <Typography variant='h6' component='div' sx={{ fontWeight: 100 }}>
                  {t(mission.label)}
                </Typography>
                {/* <Typography variant='caption' sx={{ fontSize: '0.7rem', mb: 0.25, fontWeight: 100 }}>
                  <Trans
                    i18nKey='invoice:createInvoice.invoiceDeliveryMethodCount'
                    values={{
                      count: mission.count,
                      totalCount: invoiceCountStats?.totalCount,
                    }}
                  />
                </Typography> */}
              </Button>
            </Grid>
          ))}
        </Grid>
      }
    />
  );
};
